import { useMemo } from 'react'
import type { ColDef } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  DataGrid,
  GridTextCell,
  GridNumberCell,
  GridDateCell,
} from '@/components/common/grid'
import { TYPE_BADGE_VARIANT, STATUS_BADGE_VARIANT } from './constants'
import type { StoreModelRead, ModelType, ModelStatus } from '@/types'

function TypeBadgeCell(props: CustomCellRendererProps<StoreModelRead>) {
  const type = props.value as ModelType
  if (!type) return null

  return <Badge variant={TYPE_BADGE_VARIANT[type]}>{type}</Badge>
}

function StatusBadgeCell(props: CustomCellRendererProps<StoreModelRead>) {
  const status = props.value as ModelStatus
  if (!status) return null

  const isActivelyProcessing =
    (status === 'PROCESSING' || status === 'PENDING') &&
    props.context?.processingModelId === props.data?.id

  return (
    <div className="flex items-center gap-2">
      <Badge variant={STATUS_BADGE_VARIANT[status]}>{status}</Badge>
      {isActivelyProcessing && (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  )
}

interface StoreModelsGridProps {
  models: StoreModelRead[]
  onRowClicked: (model: StoreModelRead) => void
  loading?: boolean
  processingModelId?: string | null
}

export function StoreModelsGrid({
  models,
  onRowClicked,
  loading,
  processingModelId,
}: StoreModelsGridProps) {
  const gridContext = useMemo(
    () => ({ processingModelId }),
    [processingModelId]
  )
  const columnDefs: ColDef<StoreModelRead>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        minWidth: 180,
        cellRenderer: GridTextCell,
      },
      {
        field: 'model_type',
        headerName: 'Type',
        flex: 1,
        minWidth: 100,
        maxWidth: 120,
        cellRenderer: TypeBadgeCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 110,
        maxWidth: 140,
        cellRenderer: StatusBadgeCell,
      },
      {
        field: 'store_count',
        headerName: 'Stores',
        flex: 1,
        minWidth: 80,
        maxWidth: 100,
        cellRenderer: GridNumberCell,
      },
      {
        field: 'group_count',
        headerName: 'Groups',
        flex: 1,
        minWidth: 80,
        maxWidth: 100,
        cellRenderer: GridNumberCell,
      },
      {
        field: 'allocation_basis',
        headerName: 'Allocation Basis',
        flex: 1,
        minWidth: 130,
        maxWidth: 160,
        cellRenderer: GridTextCell,
      },
      {
        field: 'creator',
        headerName: 'Creator',
        flex: 1,
        minWidth: 120,
        maxWidth: 160,
        valueGetter: params => {
          const creator = params.data?.creator
          return creator?.display_name || creator?.email || ''
        },
        cellRenderer: GridTextCell,
      },
      {
        field: 'created_at',
        headerName: 'Created',
        flex: 1,
        minWidth: 130,
        maxWidth: 160,
        cellRenderer: GridDateCell,
      },
    ],
    []
  )

  return (
    <DataGrid
      rowData={models}
      columnDefs={columnDefs}
      onRowClicked={onRowClicked}
      loading={loading}
      noRowsMessage="No store models found"
      context={gridContext}
    />
  )
}
